import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Eye, MousePointer, Clock, TrendingUp, X, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

interface PopupAnalyticsViewProps {
  companyId: string;
}

interface PopupData {
  id: string;
  title: string;
  category: string;
  delivered_count: number;
  viewed_count: number;
  completed_count: number;
  dismissed_count: number;
  avg_time_to_action: number;
  created_at: string;
}

export function PopupAnalyticsView({ companyId }: PopupAnalyticsViewProps) {
  const [popups, setPopups] = useState<PopupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPopups: 0,
    avgViewRate: 0,
    avgCompletionRate: 0,
    avgTimeToAction: 0
  });

  useEffect(() => {
    loadPopupAnalytics();
  }, [companyId]);

  const loadPopupAnalytics = async () => {
    try {
      const { data: interventions, error } = await supabase
        .from('interventions')
        .select('*')
        .eq('company_id', companyId)
        .eq('delivery_method', 'popup')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Group by title and aggregate metrics
      const popupMap = new Map<string, PopupData>();
      
      (interventions || []).forEach((intervention: any) => {
        const key = intervention.title;
        const existing = popupMap.get(key);
        
        const isViewed = !!intervention.viewed_at;
        const isCompleted = !!intervention.completed_at;
        const isDismissed = !!intervention.dismissed_at;
        const timeToAction = intervention.viewed_at && intervention.completed_at
          ? (new Date(intervention.completed_at).getTime() - new Date(intervention.viewed_at).getTime()) / 1000
          : 0;

        if (existing) {
          existing.delivered_count++;
          if (isViewed) existing.viewed_count++;
          if (isCompleted) existing.completed_count++;
          if (isDismissed) existing.dismissed_count++;
          if (timeToAction > 0) {
            existing.avg_time_to_action = (existing.avg_time_to_action + timeToAction) / 2;
          }
        } else {
          popupMap.set(key, {
            id: intervention.id,
            title: intervention.title,
            category: intervention.category,
            delivered_count: 1,
            viewed_count: isViewed ? 1 : 0,
            completed_count: isCompleted ? 1 : 0,
            dismissed_count: isDismissed ? 1 : 0,
            avg_time_to_action: timeToAction,
            created_at: intervention.created_at
          });
        }
      });

      const popupList = Array.from(popupMap.values());
      setPopups(popupList);

      // Calculate overall stats
      const totalDelivered = popupList.reduce((sum, p) => sum + p.delivered_count, 0);
      const totalViewed = popupList.reduce((sum, p) => sum + p.viewed_count, 0);
      const totalCompleted = popupList.reduce((sum, p) => sum + p.completed_count, 0);
      const avgTime = popupList.reduce((sum, p) => sum + p.avg_time_to_action, 0) / (popupList.length || 1);

      setStats({
        totalPopups: popupList.length,
        avgViewRate: totalDelivered > 0 ? (totalViewed / totalDelivered) * 100 : 0,
        avgCompletionRate: totalViewed > 0 ? (totalCompleted / totalViewed) * 100 : 0,
        avgTimeToAction: avgTime
      });
    } catch (error) {
      console.error('Error loading popup analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading popup analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">View Rate</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.avgViewRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <MousePointer className="h-4 w-4 text-green-500" />
              <span className="text-sm text-muted-foreground">Completion Rate</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.avgCompletionRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-muted-foreground">Avg Time to Action</span>
            </div>
            <p className="text-2xl font-bold mt-1">{formatTime(stats.avgTimeToAction)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-muted-foreground">Total Popups</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.totalPopups}</p>
          </CardContent>
        </Card>
      </div>

      {/* Popup List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Popup Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {popups.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No popup interventions found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-center">Delivered</TableHead>
                  <TableHead className="text-center">Viewed</TableHead>
                  <TableHead className="text-center">Completed</TableHead>
                  <TableHead className="text-center">Dismissed</TableHead>
                  <TableHead className="text-center">Engagement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {popups.map((popup) => {
                  const viewRate = popup.delivered_count > 0 
                    ? (popup.viewed_count / popup.delivered_count) * 100 
                    : 0;
                  const completionRate = popup.viewed_count > 0 
                    ? (popup.completed_count / popup.viewed_count) * 100 
                    : 0;

                  return (
                    <TableRow key={popup.id}>
                      <TableCell className="font-medium">{popup.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{popup.category}</Badge>
                      </TableCell>
                      <TableCell className="text-center">{popup.delivered_count}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Eye className="h-3 w-3 text-muted-foreground" />
                          {popup.viewed_count}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          {popup.completed_count}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <X className="h-3 w-3 text-red-500" />
                          {popup.dismissed_count}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant={completionRate >= 50 ? "default" : "secondary"}
                          className={completionRate >= 50 ? "bg-green-500" : ""}
                        >
                          {completionRate.toFixed(0)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
